import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc, onSnapshot, getCountFromServer, collection, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { leaderboardService } from '../services/leaderboardService';
import LeaderboardDisplay from '../components/leaderboard/LeaderboardDisplay';
import DefaultLoading from '../common/DefaultLoading';
import AlertError from '../dashboard/PermissionDenied';
import { motion, AnimatePresence } from 'framer-motion';
import CardFrame from '../games/components/CardFrame';
import StartEndBackground from '../games/components/StartEndBackground';
import { QRCodeSVG } from 'qrcode.react';
import { useFont } from '../contexts/FontContext';
import {
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  QrCodeIcon,
  Cog6ToothIcon,
  GiftIcon,
} from '@heroicons/react/24/solid';

const formatScore = (score) => score.toString();

// Generate confetti particles deterministically from a seed string
function generateConfetti(count, colors) {
  const particles = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 + (i * 0.7);
    const distance = 300 + Math.random() * 600;
    particles.push({
      id: i,
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance - 200,
      rotation: Math.random() * 1080 - 540,
      color: colors[i % colors.length],
      size: 6 + Math.random() * 10,
      aspectRatio: Math.random() > 0.5 ? 1 : 0.4 + Math.random() * 0.4,
      duration: 2 + Math.random() * 2,
      delay: Math.random() * 0.3,
    });
  }
  return particles;
}

// Full-screen celebration overlay for new #1 score
function NewHighScoreOverlay({ entry, colors, fontFamily }) {
  const confettiColors = useMemo(
    () => [colors.primaryAction, colors.secondaryAction, '#FFD700', '#FFFFFF', colors.primaryAction + 'CC'],
    [colors.primaryAction, colors.secondaryAction]
  );
  const particles = useMemo(() => generateConfetti(45, confettiColors), [confettiColors]);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.03 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
    >
      {/* Dark scrim backdrop */}
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
      />
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-sm"
          style={{ width: p.size, height: p.size * p.aspectRatio, backgroundColor: p.color }}
          initial={{ x: 0, y: 0, rotate: 0, opacity: 1, scale: 0 }}
          animate={{ x: p.x, y: p.y + 400, rotate: p.rotation, opacity: [0, 1, 1, 0], scale: [0, 1.2, 1, 0.6] }}
          transition={{ duration: p.duration, delay: p.delay + 0.15, ease: 'easeOut', opacity: { times: [0, 0.1, 0.7, 1] }, scale: { times: [0, 0.15, 0.5, 1] } }}
        />
      ))}
      <div className="relative z-10 text-center">
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: [0, 1.15, 1], opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6, type: 'spring', damping: 12, stiffness: 200 }}
          className="mb-4"
        >
          <span
            className="inline-block px-8 py-3 rounded-2xl text-4xl sm:text-5xl lg:text-7xl xl:text-8xl font-black uppercase tracking-wider"
            style={{ backgroundColor: colors.primaryAction, color: colors.buttonText, textShadow: '0 2px 20px rgba(0,0,0,0.3)', fontFamily }}
          >
            {entry.rank === 1 ? 'New High Score!' : 'New Score!'}
          </span>
        </motion.div>
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3"
          style={{ color: colors.buttonText, fontFamily, textShadow: '0 4px 30px rgba(0,0,0,0.5)' }}
        >
          {entry.name}
        </motion.div>
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.span
            className="inline-block text-5xl sm:text-6xl lg:text-8xl xl:text-9xl font-black tabular-nums"
            style={{ color: colors.secondaryAction, fontFamily, textShadow: '0 4px 40px rgba(0,0,0,0.4)' }}
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ delay: 1.2, duration: 1, repeat: 3, ease: 'easeInOut' }}
          >
            {entry.displayScore}
          </motion.span>
        </motion.div>
      </div>
    </motion.div>
  );
}

// Single-name winner picker for live giveaway draws. Cycles names from the
// locally-loaded entries as visual filler while pickRandomEntry runs server-
// side over the full collection (one document read). Reveals once both the
// real winner has returned AND a minimum spin time has elapsed.
const PICK_MIN_SPIN_MS = 5000;

function WinnerPickerOverlay({ entries, gameId, colors, fontFamily, onDismiss }) {
  const [phase, setPhase] = useState('spinning'); // 'spinning' | 'revealed' | 'empty'
  const [displayIndex, setDisplayIndex] = useState(0);
  const [winner, setWinner] = useState(null);
  const spinStartRef = useRef(Date.now());

  // Fetch the actual winner from the full collection in parallel with the spin
  useEffect(() => {
    let cancelled = false;
    spinStartRef.current = Date.now();
    (async () => {
      try {
        const picked = await leaderboardService.pickRandomEntry(gameId);
        if (cancelled) return;
        if (!picked) {
          setPhase('empty');
          return;
        }
        const elapsed = Date.now() - spinStartRef.current;
        const remaining = Math.max(0, PICK_MIN_SPIN_MS - elapsed);
        setTimeout(() => {
          if (cancelled) return;
          setWinner(picked);
          setPhase('revealed');
        }, remaining);
      } catch (err) {
        if (cancelled) return;
        console.error('pickRandomEntry failed', err);
        setPhase('empty');
      }
    })();
    return () => { cancelled = true; };
  }, [gameId]);

  // Cycle through visible names as visual filler, slowing toward the end
  useEffect(() => {
    if (phase !== 'spinning' || !entries || entries.length === 0) return;
    let cancelled = false;
    let delay = 60;

    const step = () => {
      if (cancelled) return;
      setDisplayIndex(i => (i + 1 + Math.floor(Math.random() * Math.max(1, entries.length - 1))) % entries.length);
      const elapsed = Date.now() - spinStartRef.current;
      const t = Math.min(1, elapsed / PICK_MIN_SPIN_MS);
      // Ease delay outward: starts at 60ms, ends near 260ms
      delay = 60 + Math.pow(t, 2) * 200;
      setTimeout(step, delay);
    };
    const t = setTimeout(step, delay);
    return () => { cancelled = true; clearTimeout(t); };
  }, [phase, entries]);

  const confettiColors = useMemo(
    () => [colors.primaryAction, colors.secondaryAction, '#FFD700', '#FFFFFF', colors.primaryAction + 'CC'],
    [colors.primaryAction, colors.secondaryAction]
  );
  const particles = useMemo(() => phase === 'revealed' ? generateConfetti(60, confettiColors) : [], [phase, confettiColors]);

  if (phase === 'empty') {
    return (
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center cursor-pointer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={onDismiss}
      >
        <div
          className="absolute inset-0"
          style={{ backgroundColor: colors.background, opacity: 0.92, backdropFilter: 'blur(6px)' }}
        />
        <div className="relative z-10 text-center px-6" style={{ fontFamily, color: colors.buttonText }}>
          <div className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: colors.primaryAction }}>
            No entries to pick from
          </div>
          <div className="text-lg opacity-70">Click anywhere to dismiss</div>
        </div>
      </motion.div>
    );
  }

  const currentEntry = entries && entries.length > 0 ? entries[displayIndex % entries.length] : null;
  const shown = phase === 'revealed' ? winner : currentEntry;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      onClick={phase === 'revealed' ? onDismiss : undefined}
      style={{ cursor: phase === 'revealed' ? 'pointer' : 'default' }}
    >
      {/* Themed scrim using campaign background colour */}
      <div
        className="absolute inset-0"
        style={{ backgroundColor: colors.background, opacity: 0.92, backdropFilter: 'blur(8px)' }}
      />

      {phase === 'revealed' && particles.map(p => (
        <motion.div
          key={p.id}
          className="absolute rounded-sm"
          style={{
            left: '50%', top: '50%',
            width: p.size, height: p.size * p.aspectRatio,
            backgroundColor: p.color,
          }}
          initial={{ x: 0, y: 0, rotate: 0, opacity: 1 }}
          animate={{ x: p.x, y: p.y, rotate: p.rotation, opacity: 0 }}
          transition={{ duration: p.duration, delay: p.delay, ease: 'easeOut' }}
        />
      ))}

      <div className="relative z-10 text-center px-6 max-w-3xl">
        <motion.div
          key={phase}
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.25 }}
          className="text-[11px] sm:text-sm uppercase tracking-[0.3em] font-bold mb-4"
          style={{ color: colors.primaryAction, fontFamily }}
        >
          {phase === 'spinning' ? 'Picking a winner' : 'Winner!'}
        </motion.div>

        <motion.div
          key={`name-${phase}-${shown?.id || displayIndex}`}
          initial={phase === 'revealed' ? { scale: 0.6, opacity: 0 } : false}
          animate={phase === 'revealed' ? { scale: 1, opacity: 1 } : {}}
          transition={{ type: 'spring', damping: 14, stiffness: 280 }}
          className="font-bold leading-tight"
          style={{
            fontFamily,
            color: colors.buttonText,
            fontSize: phase === 'revealed' ? 'clamp(3rem, 10vw, 7rem)' : 'clamp(2rem, 7vw, 5rem)',
            textShadow: phase === 'revealed'
              ? `0 0 40px ${colors.primaryAction}, 0 4px 30px rgba(0,0,0,0.4)`
              : '0 2px 12px rgba(0,0,0,0.35)',
          }}
        >
          {shown?.name || shown?.displayName || '—'}
        </motion.div>

        {phase === 'revealed' && winner?.score != null && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="mt-6"
            style={{ fontFamily }}
          >
            <span
              className="text-base sm:text-xl font-bold"
              style={{ color: colors.secondaryAction }}
            >
              Score {formatScore(winner.score)}
            </span>
          </motion.div>
        )}

        {phase === 'revealed' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.4 }}
            className="mt-10 text-[11px] uppercase tracking-[0.25em]"
            style={{ color: colors.buttonText, opacity: 0.5, fontFamily }}
          >
            Click anywhere to dismiss
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// Operator controls bar
function OperatorBar({ qrVisible, onToggleQr, isFullscreen, onToggleFullscreen, onPickWinner, canPickWinner, pickPromptActive, themeColors }) {
  return (
    <motion.div
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 80, opacity: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl shadow-2xl max-w-[calc(100vw-2rem)]"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(12px)' }}
    >
      {/* Pick-winner callout shown only when the operator arrived via the
          dashboard "Pick winner" button. Positioned above the bar so it
          reads "Click here when you're ready to record". */}
      <AnimatePresence>
        {pickPromptActive && canPickWinner && (
          <motion.div
            initial={{ y: 10, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 10, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 280 }}
            className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full whitespace-nowrap"
            role="status"
            aria-live="polite"
          >
            <div
              className="px-4 py-2.5 rounded-xl text-sm font-semibold shadow-2xl"
              style={{
                backgroundColor: themeColors?.primaryAction || '#7C3AED',
                color: themeColors?.buttonText || '#FFFFFF',
              }}
            >
              Ready when you are. Hit record, then click Pick winner.
            </div>
            <div
              className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-3 rotate-45"
              style={{ backgroundColor: themeColors?.primaryAction || '#7C3AED' }}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <button
        onClick={onToggleQr}
        title={qrVisible ? 'Hide QR code' : 'Show QR code'}
        aria-label={qrVisible ? 'Hide QR code' : 'Show QR code'}
        className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-colors"
        style={{ backgroundColor: qrVisible ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)', color: '#fff' }}
      >
        <QrCodeIcon className="w-4 h-4" />
        <span className="hidden sm:inline">QR {qrVisible ? 'On' : 'Off'}</span>
        <span className="sm:hidden">QR</span>
      </button>
      <button
        onClick={onToggleFullscreen}
        title={isFullscreen ? 'Exit full screen' : 'Enter full screen'}
        aria-label={isFullscreen ? 'Exit full screen' : 'Enter full screen'}
        className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-colors"
        style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' }}
      >
        {isFullscreen ? <ArrowsPointingInIcon className="w-4 h-4" /> : <ArrowsPointingOutIcon className="w-4 h-4" />}
        <span className="hidden sm:inline">{isFullscreen ? 'Exit' : 'Fullscreen'}</span>
      </button>
      <div className="hidden sm:block h-6 w-px bg-white/20" />
      <motion.button
        onClick={onPickWinner}
        disabled={!canPickWinner}
        title={canPickWinner ? 'Pick a random winner from the leaderboard' : 'Need at least 5 entries'}
        aria-label="Pick a random winner from the leaderboard"
        className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        style={{
          backgroundColor: pickPromptActive && canPickWinner
            ? (themeColors?.primaryAction || '#7C3AED')
            : 'rgba(255,255,255,0.15)',
          color: pickPromptActive && canPickWinner
            ? (themeColors?.buttonText || '#FFFFFF')
            : '#fff',
        }}
        animate={pickPromptActive && canPickWinner ? {
          scale: [1, 1.06, 1],
          boxShadow: [
            `0 0 0 0 ${themeColors?.primaryAction || '#7C3AED'}66`,
            `0 0 0 10px ${themeColors?.primaryAction || '#7C3AED'}00`,
            `0 0 0 0 ${themeColors?.primaryAction || '#7C3AED'}00`,
          ],
        } : { scale: 1 }}
        transition={pickPromptActive && canPickWinner ? {
          duration: 1.6,
          repeat: Infinity,
          ease: 'easeInOut',
        } : { duration: 0.2 }}
      >
        <GiftIcon className="w-4 h-4" />
        <span className="hidden sm:inline">Pick winner</span>
        <span className="sm:hidden">Win</span>
      </motion.button>
      <div className="hidden sm:block h-6 w-px bg-white/20" />
      <span className="hidden sm:inline text-[10px] text-white/40 font-mono">Q qr | F full | W win | O close</span>
    </motion.div>
  );
}

// Continuously scrolling list of entries — seamless loop via duplicated content.
// Stays static (no animation) when content fits the container.
function ScrollTicker({ entries, theme, campaign, highlightedEntryIds, rankChanges, newEntryIds }) {
  const containerRef = useRef(null);
  const copyRef = useRef(null);
  const [loop, setLoop] = useState(false);
  const [copyHeight, setCopyHeight] = useState(0);

  useEffect(() => {
    const measure = () => {
      const c = containerRef.current;
      const copy = copyRef.current;
      if (!c || !copy) return;
      const contentH = copy.getBoundingClientRect().height;
      const containerH = c.clientHeight;
      setCopyHeight(contentH);
      setLoop(contentH > containerH + 1);
    };
    measure();
    const t = setTimeout(measure, 250);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', measure);
    };
  }, [entries.length]);

  const duration = copyHeight > 0 ? copyHeight / TICKER_PX_PER_SECOND : 0;

  const list = (refProp) => (
    <div ref={refProp}>
      <LeaderboardDisplay
        entries={entries}
        userEntry={null}
        theme={theme}
        game={campaign}
        onJoinLeaderboard={() => {}}
        hasJoined={false}
        showTitle={false}
        showTableHead={false}
        naturalHeight
        seamless
        large={true}
        highlightedEntryIds={highlightedEntryIds}
        rankChanges={rankChanges}
        newEntryIds={newEntryIds}
      />
    </div>
  );

  return (
    <div ref={containerRef} className="flex-1 min-h-0 overflow-hidden relative">
      <motion.div
        animate={loop && copyHeight > 0 ? { y: [0, -copyHeight] } : { y: 0 }}
        transition={loop && copyHeight > 0 ? { duration, repeat: Infinity, ease: 'linear' } : { duration: 0 }}
      >
        {list(copyRef)}
        {loop && <div aria-hidden="true">{list()}</div>}
      </motion.div>
    </div>
  );
}

const CELEBRATION_DURATION = 7000;
const IDLE_THRESHOLD = 60000; // 60 seconds before attract mode
const TICKER_PX_PER_SECOND = 18; // scroll speed of the ranks 4+ ticker on TV

const LeaderboardPage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loadFont } = useFont();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [leaderboardEntries, setLeaderboardEntries] = useState([]);
  const [totalPlayerCount, setTotalPlayerCount] = useState(0);
  const [newTopScore, setNewTopScore] = useState(null);
  const [highlightedEntryIds, setHighlightedEntryIds] = useState(new Set());
  const [newEntryIds, setNewEntryIds] = useState(new Set());
  const [rankChanges, setRankChanges] = useState(new Map()); // id -> delta (positive = moved up)
  const previousEntriesMapRef = useRef(null);
  const previousRanksRef = useRef(new Map()); // id -> previous rank
  const celebrationQueueRef = useRef([]);
  const celebrationActiveRef = useRef(false);
  const highlightTimeoutRef = useRef(null);
  const newEntryTimerRef = useRef(null);

  // Operator state
  const [qrVisible, setQrVisible] = useState(true);
  const [controlsVisible, setControlsVisible] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [winnerOverlayVisible, setWinnerOverlayVisible] = useState(false);
  // When arriving via ?pick=1 we keep the operator bar pinned open with a
  // callout pointing at the Pick winner button — gives the creator time to
  // start screen recording before they trigger the spin.
  const [pickPromptActive, setPickPromptActive] = useState(false);
  const [cursorHidden, setCursorHidden] = useState(false);
  const cursorTimerRef = useRef(null);
  const controlsTimerRef = useRef(null);
  const containerRef = useRef(null);

  // Latest player callout
  const [latestEntry, setLatestEntry] = useState(null);
  const latestTimerRef = useRef(null);

  // Attract/idle mode
  const lastNewEntryTimeRef = useRef(Date.now());
  const [isIdle, setIsIdle] = useState(false);

  // Viewport-bounded card height (CardFrame doesn't propagate h-full reliably)
  const leaderboardBodyRef = useRef(null);
  const [scrollMaxHeight, setScrollMaxHeight] = useState(null);

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        const campaignDoc = await getDoc(doc(db, 'campaign', id));
        if (campaignDoc.exists()) {
          setCampaign({ id: campaignDoc.id, ...campaignDoc.data() });
          setLeaderboardEntries([]);
        } else {
          setError('Campaign not found');
        }
      } catch (err) {
        console.error('Error fetching campaign:', err);
        setError('Failed to load campaign');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchCampaign();
    else { setError('No campaign ID provided'); setLoading(false); }
  }, [id]);

  const processQueue = useCallback(() => {
    if (celebrationActiveRef.current) return;
    const next = celebrationQueueRef.current.shift();
    if (!next) return;
    celebrationActiveRef.current = true;
    setNewTopScore(next);
    setTimeout(() => {
      setNewTopScore(null);
      celebrationActiveRef.current = false;
      setTimeout(processQueue, 500);
    }, CELEBRATION_DURATION);
  }, []);

  useEffect(() => {
    const primaryFont = campaign?.options?.theme?.typography?.primaryFont;
    if (primaryFont) loadFont(primaryFont);
  }, [campaign, loadFont]);

  // Subscribe to leaderboard
  useEffect(() => {
    if (!campaign) return;

    let active = true;
    const leaderboardRef = collection(db, 'leaderboard', campaign.id, 'entries');
    const leaderboardQuery = query(leaderboardRef, orderBy('score', 'desc'), limit(25));

    const fetchCount = async () => {
      try {
        const countSnapshot = await getCountFromServer(leaderboardRef);
        if (!active) return;
        setTotalPlayerCount(countSnapshot.data().count);
      } catch (err) {
        // Non-fatal — count is a cosmetic display
      }
    };

    const handleSnapshot = (snapshot) => {
      try {
        if (!active) return;

        const entries = snapshot.docs.map((d, index) => ({
          id: d.id,
          ...d.data(),
          rank: index + 1,
          displayScore: formatScore(d.data().score),
        }));

        const currentMap = new Map(entries.map((e) => [e.id, e.score]));
        const isFirstFetch = previousEntriesMapRef.current === null;

        if (!isFirstFetch && entries.length > 0) {
          const prevMap = previousEntriesMapRef.current;
          const celebrateEntries = entries.filter((e) => {
            if (e.rank > 20) return false;
            const prevScore = prevMap.get(e.id);
            return prevScore === undefined || e.score > prevScore;
          });

          if (celebrateEntries.length > 0) {
            // New activity detected -- reset idle timer
            lastNewEntryTimeRef.current = Date.now();
            setIsIdle(false);

            celebrationQueueRef.current.push(...celebrateEntries);
            processQueue();

            const ids = new Set(celebrateEntries.map((e) => e.id));
            if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
            setHighlightedEntryIds(ids);
            highlightTimeoutRef.current = setTimeout(() => setHighlightedEntryIds(new Set()), CELEBRATION_DURATION);

            // Latest player callout (most recent entry)
            const latest = celebrateEntries[celebrateEntries.length - 1];
            setLatestEntry(latest);
            if (latestTimerRef.current) clearTimeout(latestTimerRef.current);
            latestTimerRef.current = setTimeout(() => setLatestEntry(null), 5000);
          }
        }

        // Track rank changes and new entries
        if (!isFirstFetch) {
          const prevRanks = previousRanksRef.current;
          const changes = new Map();
          const brandNew = new Set();
          entries.forEach((e) => {
            const prevRank = prevRanks.get(e.id);
            if (prevRank === undefined) {
              brandNew.add(e.id);
            } else if (prevRank !== e.rank) {
              changes.set(e.id, prevRank - e.rank); // positive = moved up
            }
          });
          if (changes.size > 0) setRankChanges(changes);
          if (brandNew.size > 0) {
            setNewEntryIds(brandNew);
            if (newEntryTimerRef.current) clearTimeout(newEntryTimerRef.current);
            newEntryTimerRef.current = setTimeout(() => setNewEntryIds(new Set()), 3000);
          }
          // Clear rank changes after 4s
          if (changes.size > 0) setTimeout(() => setRankChanges(new Map()), 4000);
        }

        previousRanksRef.current = new Map(entries.map((e) => [e.id, e.rank]));
        previousEntriesMapRef.current = currentMap;
        setLeaderboardEntries(entries);
        // Re-count only when entries actually changed (snapshot tells us
        // that new players joined). Costs one count read per change vs
        // the previous interval that fired every 30s regardless.
        if (!isFirstFetch && snapshot.docChanges().length > 0) {
          fetchCount();
        }
      } catch (err) {
        console.error('Error processing leaderboard snapshot:', err);
      }
    };

    const unsubscribe = onSnapshot(
      leaderboardQuery,
      handleSnapshot,
      (err) => console.error('Leaderboard subscription error:', err)
    );
    // Initial count, then refresh only when the onSnapshot fires a change.
    // The previous 30s interval billed a count read every 30s for as long
    // as a kiosk/TV display was open — at 10 screens running 8h/day that
    // was ~10k count reads/day for a cosmetic player-count badge.
    fetchCount();

    return () => {
      active = false;
      unsubscribe();
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
      if (latestTimerRef.current) clearTimeout(latestTimerRef.current);
      if (newEntryTimerRef.current) clearTimeout(newEntryTimerRef.current);
    };
  }, [campaign, processQueue]);

  // Attract mode only when no entries at all
  useEffect(() => {
    setIsIdle(leaderboardEntries.length === 0);
  }, [leaderboardEntries.length]);

  // Compute a viewport-bounded max-height for the leaderboard body so the
  // card never expands off-screen. CardFrame doesn't propagate h-full through
  // its wrappers, so flex-1 min-h-0 alone can't constrain the card reliably.
  useEffect(() => {
    const measure = () => {
      const el = leaderboardBodyRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // Account for: card inner bottom padding (~40px) + outer page py padding
      // (~32px) + small safety gap. 96px handles the largest combination.
      const available = vh - rect.top - 96;
      setScrollMaxHeight(Math.max(200, available));
    };
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(measure);
      return () => cancelAnimationFrame(raf2);
    });
    const t = setTimeout(measure, 250);
    window.addEventListener('resize', measure);
    document.addEventListener('fullscreenchange', measure);
    return () => {
      cancelAnimationFrame(raf1);
      clearTimeout(t);
      window.removeEventListener('resize', measure);
      document.removeEventListener('fullscreenchange', measure);
    };
  }, [leaderboardEntries.length, isFullscreen, qrVisible]);

  // Fullscreen API
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.().catch(() => {});
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Auto-hide cursor after 3s of no movement
  const resetCursorTimer = useCallback(() => {
    setCursorHidden(false);
    if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
    cursorTimerRef.current = setTimeout(() => setCursorHidden(true), 3000);
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', resetCursorTimer);
    return () => {
      window.removeEventListener('mousemove', resetCursorTimer);
      if (cursorTimerRef.current) clearTimeout(cursorTimerRef.current);
    };
  }, [resetCursorTimer]);

  // Auto-hide controls after 10s — unless we're in the pick-prompt state,
  // where we want the bar pinned until the creator clicks Pick winner.
  useEffect(() => {
    if (!controlsVisible) return;
    if (pickPromptActive) return;
    if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current);
    controlsTimerRef.current = setTimeout(() => setControlsVisible(false), 10000);
    return () => { if (controlsTimerRef.current) clearTimeout(controlsTimerRef.current); };
  }, [controlsVisible, pickPromptActive]);

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      switch (e.key.toLowerCase()) {
        case 'f': toggleFullscreen(); break;
        case 'o': setControlsVisible(v => !v); break;
        case 'q': setQrVisible(v => !v); break;
        case 'w': setWinnerOverlayVisible(v => !v); break;
        default: break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggleFullscreen]);

  // Arrive with ?pick=1 from the dashboard "Pick winner" button: pin the
  // operator bar open and highlight the Pick winner button with a callout,
  // rather than auto-firing the spin. Gives the creator time to start
  // screen-recording. Strip the param so a refresh doesn't re-trigger.
  const pickPromptArmedRef = useRef(false);
  useEffect(() => {
    if (pickPromptArmedRef.current) return;
    if (!campaign) return;
    if (searchParams.get('pick') !== '1') return;
    pickPromptArmedRef.current = true;
    setControlsVisible(true);
    setPickPromptActive(true);
    const next = new URLSearchParams(searchParams);
    next.delete('pick');
    setSearchParams(next, { replace: true });
  }, [campaign, searchParams, setSearchParams]);

  if (loading) return <DefaultLoading />;
  if (error) return <AlertError text={error} />;
  if (!campaign) return <AlertError text="Campaign not found" />;

  const colors = {
    background: campaign?.options?.customColors?.background || '#4B5563',
    surface: campaign?.options?.customColors?.surface || '#FFFFFF',
    primary: campaign?.options?.customColors?.primary || '#4B5563',
    text: campaign?.options?.customColors?.text || '#4B5563',
    primaryAction: campaign?.options?.customColors?.primaryAction || '#7C3AED',
    secondaryAction: campaign?.options?.customColors?.secondaryAction || '#F97316',
    buttonText: campaign?.options?.customColors?.buttonText || '#FFFFFF',
  };

  const theme = {
    fonts: {
      title: campaign?.options?.theme?.typography?.primaryFont || 'Inter',
      subtitle: campaign?.options?.theme?.typography?.primaryFont || 'Inter',
      button: campaign?.options?.theme?.typography?.primaryFont || 'Inter',
    },
    colors: {
      outerBackground: colors.background,
      outerOpacity: 0.2,
      cardBackground: colors.surface,
      cardOpacity: 1,
      titleText: colors.primary,
      subtitleText: colors.text,
      buttonBackground: colors.secondaryAction,
      buttonText: colors.buttonText,
    },
    typography: {
      primaryFont: campaign?.options?.theme?.typography?.primaryFont || 'Inter',
    },
  };

  const frameStyle = campaign?.options?.frameStyle || 'clean';
  const overlayEffect = campaign?.options?.cardOverlayEffect || null;
  const backgroundPattern = campaign?.options?.backgroundPattern || null;
  const backgroundImage = campaign?.options?.backgroundImage || null;
  const playUrl = `${window.location.origin}/p/${campaign.id}`;
  const topScore = leaderboardEntries[0];

  return (
    <>
    <Helmet>
      <title>{campaign?.options?.title ? `${campaign.options.title}: Live leaderboard` : 'Live leaderboard'}</title>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    <div
      ref={containerRef}
      className={`relative flex flex-1 min-h-0 flex-col overflow-hidden px-4 py-6 lg:px-8 lg:py-8 ${cursorHidden ? 'cursor-none' : ''}`}
      style={{ backgroundColor: colors.background, fontFamily: theme.fonts.title }}
    >
      <StartEndBackground backgroundImage={backgroundImage} backgroundColor={colors.background} />

      {/* Celebration overlay */}
      <AnimatePresence>
        {newTopScore && (
          <NewHighScoreOverlay entry={newTopScore} colors={colors} fontFamily={theme.fonts.title} />
        )}
      </AnimatePresence>

      {/* Attract/idle mode */}
      <AnimatePresence>
        {isIdle && !newTopScore && (
          <motion.div
            key="attract"
            className="fixed inset-0 z-30 overflow-y-auto"
            style={{ backgroundColor: colors.background }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            <StartEndBackground backgroundImage={backgroundImage} backgroundColor={colors.background} />
            <div className="relative z-10 min-h-full flex items-center justify-center p-4 sm:p-6">
              <div className="w-full max-w-xl">
                <CardFrame frameStyle={frameStyle} colors={colors} animate={false} fullWidth={true} overlayEffect={overlayEffect} backgroundPattern={backgroundPattern}>
                  <div className="flex flex-col items-center text-center px-6 py-6 sm:py-8 lg:px-10 lg:py-10">
                    <motion.h2
                      className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 lg:mb-8 line-clamp-2 break-words max-w-full"
                      style={{ color: colors.primary, fontFamily: theme.fonts.title }}
                    >
                      {campaign.options?.title || 'Play now!'}
                    </motion.h2>
                    <motion.div
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                      className="bg-white p-4 sm:p-6 lg:p-8 rounded-3xl shadow-2xl"
                    >
                      <QRCodeSVG value={playUrl} size={240} bgColor="#ffffff" fgColor="#2c3e50" level="M" className="w-40 h-40 sm:w-48 sm:h-48 lg:w-60 lg:h-60 xl:w-64 xl:h-64" />
                    </motion.div>
                    <p
                      className="mt-6 lg:mt-8 text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-semibold"
                      style={{ color: colors.primaryAction, fontFamily: theme.fonts.title }}
                    >
                      Scan to play
                    </p>
                    <p className="mt-2 text-base sm:text-lg lg:text-xl opacity-70" style={{ color: colors.text }}>
                      Use your phone camera
                    </p>
                  </div>
                </CardFrame>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Latest player callout */}
      <AnimatePresence>
        {latestEntry && !newTopScore && (
          <motion.div
            key={latestEntry.id}
            initial={{ y: -60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -60, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-30 px-6 py-3 rounded-2xl shadow-xl"
            style={{ backgroundColor: colors.primaryAction, color: colors.buttonText }}
          >
            <span className="text-lg lg:text-xl xl:text-2xl font-bold" style={{ fontFamily: theme.fonts.title }}>
              {latestEntry.name} scored {latestEntry.displayScore}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content -- portrait: stack with compact QR; landscape: side-by-side */}
      <div className="relative z-10 w-full mx-auto flex-1 flex flex-col min-h-0 gap-4 lg:gap-6"
        style={{ maxWidth: '95vw' }}
      >
        {/* Leaderboard + QR sidebar */}
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-4 lg:gap-6">
          {/* Leaderboard card (title + table unified) */}
          {/* min-w-0 is critical so the title's `truncate` actually clips — without
              it, the long title forces this flex item wider than viewport and
              shoves the QR sidebar off-screen. */}
          <div className="flex-1 min-w-0 min-h-0">
            <CardFrame frameStyle={frameStyle} colors={colors} animate={false} fullWidth={true} className="h-full" overlayEffect={overlayEffect} backgroundPattern={backgroundPattern}>
              <div className="p-4 sm:p-6 lg:p-8 xl:p-10 h-full flex flex-col gap-4 lg:gap-6">
                {/* Card header: title + live indicator + compact QR on small screens */}
                <div className="flex items-start justify-between gap-4 shrink-0 pb-3 lg:pb-4" style={{ borderBottom: `1px solid ${colors.text}15` }}>
                  <div className="min-w-0 flex-1">
                    <h1
                      className="text-2xl sm:text-3xl lg:text-5xl xl:text-6xl font-bold truncate"
                      style={{ color: colors.primary, fontFamily: theme.fonts.title }}
                    >
                      {campaign.options?.title || 'Leaderboard'}
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-flex items-center gap-1.5 text-sm lg:text-lg xl:text-xl" style={{ color: colors.text }}>
                        <span className="w-2 h-2 lg:w-3 lg:h-3 bg-green-500 rounded-full animate-pulse" />
                        Live
                      </span>
                      {totalPlayerCount > 0 && (
                        <span className="text-sm lg:text-lg opacity-60" style={{ color: colors.text }}>
                          &middot; {totalPlayerCount} player{totalPlayerCount !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Compact QR -- small screens only (lg+ uses the sidebar QR) */}
                  <AnimatePresence>
                    {qrVisible && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="shrink-0 lg:hidden bg-white p-2 rounded-xl shadow-lg"
                      >
                        <QRCodeSVG value={playUrl} size={80} bgColor="#ffffff" fgColor="#2c3e50" level="M" className="w-16 h-16 sm:w-20 sm:h-20" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                {leaderboardEntries.length > 0 ? (
                  <div
                    ref={leaderboardBodyRef}
                    className="flex-1 min-h-0 overflow-hidden flex flex-col gap-3 lg:gap-4"
                    style={{ maxHeight: scrollMaxHeight ?? undefined }}
                  >
                    {/* Pinned top 3 */}
                    <div className="shrink-0">
                      <LeaderboardDisplay
                        entries={leaderboardEntries.slice(0, 3)}
                        userEntry={null}
                        theme={theme}
                        game={campaign}
                        onJoinLeaderboard={() => {}}
                        hasJoined={false}
                        showTitle={false}
                        naturalHeight
                        seamless
                        large={true}
                        highlightedEntryIds={highlightedEntryIds}
                        rankChanges={rankChanges}
                        newEntryIds={newEntryIds}
                      />
                    </div>
                    {/* Divider between pinned top 3 and the scrolling ranks 4+ */}
                    {leaderboardEntries.length > 3 && (
                      <div
                        className="shrink-0 h-px mx-2"
                        style={{ backgroundColor: `${colors.text}25` }}
                        aria-hidden="true"
                      />
                    )}
                    {/* Auto-scrolling list for ranks 4+ */}
                    {leaderboardEntries.length > 3 && (
                      <ScrollTicker
                        entries={leaderboardEntries.slice(3)}
                        theme={theme}
                        campaign={campaign}
                        highlightedEntryIds={highlightedEntryIds}
                        rankChanges={rankChanges}
                        newEntryIds={newEntryIds}
                      />
                    )}
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center py-8 lg:py-12 text-center">
                    <div className="bg-white p-4 rounded-2xl shadow-lg mb-6 lg:hidden">
                      <QRCodeSVG value={playUrl} size={120} bgColor="#ffffff" fgColor="#2c3e50" level="M" className="w-24 h-24" />
                    </div>
                    <p className="text-2xl lg:text-4xl font-bold mb-3" style={{ color: colors.primary, fontFamily: theme.fonts.title }}>
                      Be the first to play
                    </p>
                    <p className="text-base lg:text-xl opacity-60 max-w-md" style={{ color: colors.text }}>
                      {campaign.options?.intro || 'Scan the QR code with your phone camera to start playing and claim the top spot!'}
                    </p>
                  </div>
                )}
              </div>
            </CardFrame>
          </div>

          {/* QR Code sidebar -- landscape only (portrait uses the compact header QR) */}
          <AnimatePresence>
            {qrVisible && (
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 40 }}
                transition={{ duration: 0.3 }}
                className="hidden lg:flex lg:w-72 xl:w-80 2xl:w-96 flex-col gap-4 shrink-0"
              >
                <CardFrame frameStyle={frameStyle} colors={colors} animate={false} fullWidth={true} overlayEffect={overlayEffect} backgroundPattern={backgroundPattern}>
                  <div className="p-6 xl:p-8 flex flex-col items-center">
                    <div className="bg-white p-3 xl:p-4 rounded-2xl border-2" style={{ borderColor: colors.primaryAction + '20' }}>
                      <QRCodeSVG value={playUrl} size={180} bgColor="#ffffff" fgColor="#2c3e50" level="M" className="w-36 h-36 xl:w-44 xl:h-44 2xl:w-52 2xl:h-52" />
                    </div>
                    <div className="mt-4 xl:mt-6 text-center">
                      <p className="text-lg xl:text-xl 2xl:text-2xl font-semibold mb-1" style={{ color: colors.primary }}>
                        Scan to play
                      </p>
                      <p className="text-xs xl:text-sm 2xl:text-base opacity-70" style={{ color: colors.text }}>
                        Use your phone camera
                      </p>
                    </div>
                    {/* Game info */}
                    {campaign.options?.intro && (
                      <div className="mt-5 pt-5 w-full text-center" style={{ borderTop: `1px solid ${colors.text}15` }}>
                        <p className="text-sm xl:text-base opacity-60 leading-relaxed" style={{ color: colors.text }}>
                          {campaign.options.intro}
                        </p>
                      </div>
                    )}
                  </div>
                </CardFrame>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Fullscreen button (auto-hides with cursor) */}
      <AnimatePresence>
        {!cursorHidden && !controlsVisible && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={toggleFullscreen}
            className="fixed bottom-4 right-4 z-30 p-3 rounded-xl shadow-lg transition-colors"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', color: '#fff' }}
            title={isFullscreen ? 'Exit fullscreen (F)' : 'Fullscreen (F)'}
          >
            {isFullscreen ? <ArrowsPointingInIcon className="w-5 h-5" /> : <ArrowsPointingOutIcon className="w-5 h-5" />}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Operator controls gear button */}
      <AnimatePresence>
        {!cursorHidden && !controlsVisible && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setControlsVisible(true)}
            className="fixed bottom-4 left-4 z-30 p-3 rounded-xl shadow-lg transition-colors"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', color: '#fff' }}
            title="Operator controls (O)"
          >
            <Cog6ToothIcon className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Operator bar */}
      <AnimatePresence>
        {controlsVisible && (
          <OperatorBar
            qrVisible={qrVisible}
            onToggleQr={() => setQrVisible(v => !v)}
            isFullscreen={isFullscreen}
            onToggleFullscreen={toggleFullscreen}
            canPickWinner={leaderboardEntries.length >= 5}
            onPickWinner={() => { setPickPromptActive(false); setWinnerOverlayVisible(true); }}
            pickPromptActive={pickPromptActive}
            themeColors={colors}
          />
        )}
      </AnimatePresence>

      {/* Winner picker overlay */}
      <AnimatePresence>
        {winnerOverlayVisible && (
          <WinnerPickerOverlay
            entries={leaderboardEntries}
            gameId={campaign.id}
            colors={colors}
            fontFamily={theme.fonts.title}
            onDismiss={() => setWinnerOverlayVisible(false)}
          />
        )}
      </AnimatePresence>
    </div>
    </>
  );
};

export default LeaderboardPage;
